import { z } from 'zod';
import { PersonenNameSchema } from './name.js';
import {
  BuchungSchema,
  KalenderAntwortSchema,
  ModulSchema,
  RolleSchema,
  SchulungArtSchema,
  TerminFormatSchema,
} from './typen.js';

/**
 * Request-/Response-Formate der HTTP-API. Frontend und Backend nutzen dieselben Schemas.
 * Teilnehmer-Antworten enthalten nie Namen anderer Teilnehmer (F-05).
 */

/** Einheitliches Fehlerformat aller API-Endpunkte. `message` ist für Benutzer lesbar (Deutsch). */
export const ApiFehlerSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiFehler = z.infer<typeof ApiFehlerSchema>;

export const GrundSchema = z.object({
  code: z.string(),
  meldung: z.string(),
  uebergehbar: z.boolean(),
  modulId: z.number().optional(),
  buchungId: z.number().optional(),
});

export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  datenquelle: z.enum(['mock', 'graph']),
  zeit: z.string(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

export const MeResponseSchema = z.object({
  kennung: z.string(),
  name: PersonenNameSchema,
  rollen: z.array(RolleSchema),
  istTeilnehmer: z.boolean(),
  /** Nur gesetzt, wenn der Benutzer nicht auf der Teilnehmerliste steht (F-06). */
  ansprechpartner: z.string().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Termin aus Sicht eines Teilnehmers: Belegung nur als Zahl. */
export const TerminAnsichtSchema = z.object({
  id: z.number(),
  modulId: z.number(),
  beginn: z.string(),
  ende: z.string(),
  format: TerminFormatSchema,
  ort: z.string().optional(),
  teamsLink: z.string().optional(),
  trainerText: z.string().optional(),
  hinweise: z.string().optional(),
  obergrenze: z.number(),
  freiePlaetze: z.number(),
});
export type TerminAnsicht = z.infer<typeof TerminAnsichtSchema>;

export const EigeneBuchungSchema = z.object({
  id: z.number(),
  termin: TerminAnsichtSchema,
  kalenderStatus: BuchungSchema.shape.kalenderStatus,
  kalenderAntwort: KalenderAntwortSchema,
});
export type EigeneBuchung = z.infer<typeof EigeneBuchungSchema>;

export const MeineModuleResponseSchema = z.array(
  z.object({
    schulung: z.object({ id: z.number(), anzeigename: z.string(), art: SchulungArtSchema }),
    module: z.array(
      z.object({
        modul: ModulSchema,
        status: z.enum(['offen', 'gebucht']),
        kalenderAbgelehnt: z.boolean(),
        buchung: EigeneBuchungSchema.optional(),
      }),
    ),
  }),
);
export type MeineModuleResponse = z.infer<typeof MeineModuleResponseSchema>;

export const ModulTermineResponseSchema = z.object({
  modul: ModulSchema,
  /** Aktive Buchung des Teilnehmers für dieses Modul (dann wird umgebucht statt gebucht). */
  gebuchteBuchungId: z.number().optional(),
  termine: z.array(
    TerminAnsichtSchema.extend({
      aktion: z.enum(['buchen', 'umbuchen', 'gebucht', 'keine']),
      gruende: z.array(GrundSchema),
    }),
  ),
});
export type ModulTermineResponse = z.infer<typeof ModulTermineResponseSchema>;

export const BuchenRequestSchema = z.object({ terminId: z.number().int().positive() });
export type BuchenRequest = z.infer<typeof BuchenRequestSchema>;

export const UmbuchenRequestSchema = z.object({ terminId: z.number().int().positive() });
export type UmbuchenRequest = z.infer<typeof UmbuchenRequestSchema>;

/** Eine Buchung, die von einer Aktion betroffen ist (z. B. beim Entfernen eines Teilnehmers). */
export const BetroffeneBuchungSchema = z.object({
  buchungId: z.number(),
  modulTitel: z.string(),
  beginn: z.string(),
});

export const AktionsErgebnisSchema = z.object({
  buchungId: z.number(),
  /** IDs der in diesem Vorgang stornierten Buchungen (bei Umbuchung die alte). */
  storniert: z.array(z.number()),
});
export type AktionsErgebnis = z.infer<typeof AktionsErgebnisSchema>;
